/**
 * Story 020 — Catalogue popover (W7) for fast reclassify.
 *
 * MUI `<Popover>` (not `<Dialog>`) anchored to the click point. Three
 * tabs (All / Favourites / Current), search-filter live, click-row or
 * Enter-on-focused-row → PATCH each selected defect with the picked
 * entry's `defectClassId`. Star toggle persists via
 * `useToggleFavouriteMutation`; star clicks do NOT close the popover.
 *
 * Trigger paths (wired by callers):
 *   - DefectsPanel — context-menu on each row → `Reclassify` → opens
 *     here with `source="defect"` and the selection's defect ids.
 *   - FindingsPanel — same trigger, `source="aiFinding"`; the popover
 *     surfaces the AIMAP-03 "Suggest this mapping for future findings"
 *     checkbox (was AC13's "Apply mapping"). It now writes the
 *     `(classUuid → typeEntryId)` row into the catalogue's open DRAFT via
 *     the taxonomy `suggestCatalogueAiMapping` endpoint (replacing the
 *     retired lyse `PATCH /mappers`). An expert/admin-only "Apply
 *     immediately" toggle publishes the draft in the same call.
 *
 * Hard-coded behaviour:
 *   - Tab precedence (AC3): Current → Favourites → All.
 *   - Search debounce: `useDeferredValue` (50 ms-ish).
 *   - Enter on focused row → applies (AC7).
 *   - Bulk strategy (AC8): N parallel `useUpdateDefectMutation.mutateAsync`
 *     calls via `Promise.allSettled`; single snackbar with success +
 *     failure counts.
 *   - AC10: Esc + outside-click close without action.
 *   - AC11: `role="dialog"` with focus trap (MUI's Popover wires this
 *     when `disableEnforceFocus` is left at default `false`).
 *
 * D-5 gate (AC12) is enforced by the caller — the popover doesn't render
 * if the trigger refused to open. AC14 is enforced via
 * `useAiSharingGuard` (placeholder today; flips on once Story 067 lands).
 */
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCataloguesStore } from "./cataloguesStore.js";
import { useToggleFavouriteMutation } from "./queries.js";
import { useUndoableUpdateDefectMutation } from "../defects/queries.js";
import { useSuggestAiMapMutation } from "./useSuggestAiMapMutation.js";
import { useAiSharingGuard } from "./useAiSharingGuard.js";
import { useCurrentUserRole } from "@aeriosiq/auth-client";
import { useImageActivationStore } from "../analysis/state/imageActivationStore.js";
import type { DefectLike } from "./cataloguesStore.js";
import type { DefectCatalogueEntry } from "@aeriosiq/types";
import { StarIcon } from "@aeriosiq/ui/icons";

/** What kind of tile the popover was opened from. */
export type CataloguePopoverSource = "defect" | "aiFinding";

export interface CataloguePopoverSelection {
  /** Defect ids for `source==="defect"`; finding ids for `source==="aiFinding"`. */
  ids: readonly string[];
  /**
   * The AI class display name from the original ML output. Used for the
   * "Suggest…" copy/tooltip when `source==="aiFinding"`. NOT the key the
   * ai-map suggestion writes against (that is `classUuid`).
   */
  aiClassName?: string;
  /**
   * AIMAP-03 — the authoritative class key
   * (`ai_finding_model_classes.class_uuid`). Required for the ai-map suggest
   * call when `source==="aiFinding"`; the suggest endpoint keys on this, not
   * on the per-model `aiClassName` (which is not globally unique).
   */
  classUuid?: string;
}

export interface CataloguePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The inspection (data-acquisition) id — needed for the defect mutation cache. */
  inspectionId: string;
  source: CataloguePopoverSource;
  selection: CataloguePopoverSelection;
  /**
   * Defect snapshot the parent already has (used to compute the "Current"
   * tab — same `(bladeId, side)` as the first selected defect).
   * The popover reads `bladeId` + `side` + `catalogueEntryId` from each
   * entry; types are intentionally loose for parents that haven't yet
   * adopted contract 1.7.0's blade/side fields.
   */
  defectsOnInspection?: readonly DefectLike[];
}

type TabKey = "current" | "favourites" | "all";

interface ApplyOutcome {
  succeeded: number;
  failed: number;
  className: string;
}

const PADDING = 1.5;
const POPOVER_WIDTH = 360;
const POPOVER_MAX_HEIGHT = 480;

export function CataloguePopover({
  open,
  anchorEl,
  onClose,
  inspectionId,
  source,
  selection,
  defectsOnInspection,
}: CataloguePopoverProps): React.ReactElement {
  const activeCatalogueId = useCataloguesStore((s) => s.activeCatalogueId);
  const entriesByCatalogue = useCataloguesStore((s) => s.entries);
  const favouritesMap = useCataloguesStore((s) => s.favouriteCatalogueEntries);
  // Favourites + catalogue entries are already hydrated by
  // `CataloguesBootstrap` (Story 033) when the analysis page mounts —
  // the popover is a pure consumer of that cache.

  const allEntries: DefectCatalogueEntry[] = useMemo(
    () =>
      activeCatalogueId ? (entriesByCatalogue[activeCatalogueId] ?? []) : [],
    [activeCatalogueId, entriesByCatalogue],
  );
  const favouriteIds = useMemo<readonly string[]>(
    () => (activeCatalogueId ? (favouritesMap[activeCatalogueId] ?? []) : []),
    [activeCatalogueId, favouritesMap],
  );

  // "Current" — entries already used on this inspection's same (blade,
  // side). The parent passes the loaded defects so we don't re-fetch.
  const currentEntries: DefectCatalogueEntry[] = useMemo(() => {
    if (!defectsOnInspection || defectsOnInspection.length === 0) return [];
    // Use the first selected defect (or the first defect on the
    // inspection) to pick the (blade, side) context.
    const target =
      defectsOnInspection.find((d) =>
        selection.ids.includes((d as { id?: string }).id ?? ""),
      ) ?? defectsOnInspection[0];
    if (!target) return [];
    const bladeId = target.bladeId ?? null;
    const side = target.side ?? null;
    if (!bladeId || !side) return [];
    return useCataloguesStore
      .getState()
      .currentForBlade(bladeId, side, defectsOnInspection);
  }, [defectsOnInspection, selection.ids]);

  const favouriteEntries: DefectCatalogueEntry[] = useMemo(
    () => allEntries.filter((e) => favouriteIds.includes(e.entryId)),
    [allEntries, favouriteIds],
  );

  // Tab precedence (AC3) — Current if non-empty, else Favourites, else All.
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  useEffect(() => {
    if (!open) return;
    if (currentEntries.length > 0) {
      setActiveTab("current");
      return;
    }
    if (favouriteEntries.length > 0) {
      setActiveTab("favourites");
      return;
    }
    setActiveTab("all");
    // Recompute on every open. Capturing the per-tab arrays in deps is
    // intentional — the user expects the freshest precedence each time
    // they open the popover.
  }, [open, currentEntries.length, favouriteEntries.length]);

  // Search input with deferred value (AC4 — within 50ms). React 19's
  // useDeferredValue effectively debounces re-renders without a timer.
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const visibleEntries: DefectCatalogueEntry[] = useMemo(() => {
    const base =
      activeTab === "current"
        ? currentEntries
        : activeTab === "favourites"
          ? favouriteEntries
          : [...allEntries].sort((a, b) => a.name.en.localeCompare(b.name.en));
    const q = deferredSearch.trim().toLowerCase();
    if (q.length === 0) return base;
    return base.filter((e) => {
      const name = e.name.en.toLowerCase();
      // `description` is deprecated (7.10.0 / DEFCAT-R2-08, always null on the
      // wire) — `defaultDescription` is its replacement.
      const desc = e.defaultDescription?.en.toLowerCase() ?? "";
      return name.includes(q) || desc.includes(q);
    });
  }, [activeTab, currentEntries, favouriteEntries, allEntries, deferredSearch]);

  const [focusedIndex, setFocusedIndex] = useState(0);
  useEffect(() => {
    setFocusedIndex(0);
  }, [activeTab, deferredSearch]);

  const toggleFavourite = useToggleFavouriteMutation();
  const updateDefect = useUndoableUpdateDefectMutation(inspectionId);
  const suggestAiMap = useSuggestAiMapMutation();
  const guard = useAiSharingGuard();
  // Story 064 — capture the per-image undo stack key at mutate time.
  const activeImageId = useImageActivationStore((s) => s.activeImageId);

  // AIMAP-03 — who may PUBLISH the catalogue draft in one shot. Suggest is
  // open to pre_screener / expert_analyst (+ admins); "Apply immediately"
  // (which publishes) is gated to expert_analyst / tenant_admin /
  // platform_admin. The endpoint enforces this too (403 for a pre_screener
  // sending applyImmediately:true) — this just hides the affordance.
  const { role } = useCurrentUserRole();
  const canApplyImmediately =
    role === "expert_analyst" ||
    role === "tenant_admin" ||
    role === "platform_admin";

  // AIMAP-03 — "Suggest this mapping for future findings" (was AC13's "Apply
  // mapping"). Only visible when opened from an AI-finding tile, unchecked by
  // default. The optional "Apply immediately" toggle (expert/admin only)
  // publishes the catalogue draft in the same call.
  const [suggestMapping, setSuggestMapping] = useState(false);
  const [applyImmediately, setApplyImmediately] = useState(false);
  useEffect(() => {
    if (!open) {
      setSuggestMapping(false);
      setApplyImmediately(false);
    }
  }, [open]);
  // If the user un-ticks "Suggest", the dependent "Apply immediately" can't
  // stay armed.
  useEffect(() => {
    if (!suggestMapping && applyImmediately) setApplyImmediately(false);
  }, [suggestMapping, applyImmediately]);

  const [snack, setSnack] = useState<
    | { kind: "success"; copy: string }
    | { kind: "partial"; copy: string }
    | { kind: "skipped-cross-tenant"; copy: string }
    | { kind: "draft-conflict"; copy: string }
    | null
  >(null);

  const searchRef = useRef<HTMLInputElement | null>(null);
  // Auto-focus search on open (AC4). MUI's Popover focus trap will
  // hand focus to the first tab-stop; we override to the searchbox.
  const handlePopoverEntered = () => {
    searchRef.current?.focus();
  };

  const handleStarClick = (
    e: React.MouseEvent,
    entry: DefectCatalogueEntry,
  ) => {
    e.stopPropagation();
    if (!activeCatalogueId) return;
    const wasFav = favouriteIds.includes(entry.entryId);
    void toggleFavourite.mutateAsync({
      catalogueId: activeCatalogueId,
      entryId: entry.entryId,
      favourite: !wasFav,
    });
    // Popover stays open (AC6) — no onClose here.
  };

  const apply = async (entry: DefectCatalogueEntry) => {
    // AC14 — filter the selection through the cross-tenant guard. The
    // placeholder hook returns the selection unchanged today.
    const allowedIds = guard.filterAllowed(selection.ids);
    const skippedCount = selection.ids.length - allowedIds.length;

    // AIMAP-03 — fire-and-forget the ai-map SUGGEST (taxonomy) first.
    // Non-blocking; we don't await it before closing the popover. The
    // defect mutation is the load-bearing path. The suggest keys on the
    // authoritative `classUuid` (not the per-model `aiClassName`) and
    // targets the catalogue entry's TYPE entry id (`entry.entryId` — the
    // popover's entries are `entry_type='defect_type'` leaves). A 409
    // (`draft_already_exists`) is surfaced as a non-blocking message.
    if (
      source === "aiFinding" &&
      suggestMapping &&
      activeCatalogueId &&
      selection.classUuid
    ) {
      // `applyImmediately` is only honoured for roles that may publish; the
      // checkbox is hidden for others, but clamp here too (defense-in-depth).
      const wantApply = applyImmediately && canApplyImmediately;
      void suggestAiMap
        .mutateAsync({
          catalogueId: activeCatalogueId,
          classUuid: selection.classUuid,
          typeEntryId: entry.entryId,
          applyImmediately: wantApply,
        })
        .catch((err: unknown) => {
          const status = (err as { status?: number } | null)?.status;
          if (status === 409) {
            setSnack({
              kind: "draft-conflict",
              copy: "A catalogue draft is already open — ask an admin to publish or discard it, then suggest again.",
            });
          }
          // Other failures stay silent (best-effort), per AIMAP-03.
        });
    }

    let outcome: ApplyOutcome = {
      succeeded: 0,
      failed: 0,
      className: entry.name.en,
    };

    if (source === "defect" && allowedIds.length > 0) {
      // AC8 — N parallel PATCHes via Promise.allSettled. We accept that
      // the optimistic update on `defectClassId` doesn't flip the wire
      // shape (catalogueLabel lives on the server side); the row will
      // re-render correctly after `invalidateQueries` fires on settle.
      //
      // Story 064 — pass the per-defect before/after snapshots through
      // the undoable wrapper so each PATCH lands as an undo-stack
      // entry. `before` is best-effort — we read the current label
      // from the parent's `defectsOnInspection` snapshot. `imageId`
      // falls back to the empty string if the user somehow fires the
      // popover with no active image (defensive; CataloguePopover is
      // anchored to a defect row that is on the active image, so the
      // real-world value is always populated).
      const defectsById = new Map(
        (defectsOnInspection ?? []).map((d) => [
          (d as { id?: string }).id ?? "",
          d,
        ]),
      );
      const targetImageId = activeImageId ?? "";
      const results = await Promise.allSettled(
        allowedIds.map((defectId) => {
          const snapshot = defectsById.get(defectId) as
            | (DefectLike & { catalogueLabel?: string | null })
            | undefined;
          // Resolve the defect's CURRENT defectClassId via the
          // catalogue store — the row only carries `catalogueEntryId`,
          // and the inverse PATCH needs the classId to flip back.
          const currentEntry = snapshot?.catalogueEntryId
            ? useCataloguesStore
                .getState()
                .getEntryById(snapshot.catalogueEntryId)
            : null;
          const before: {
            catalogueLabel?: string;
            defectClassId?: string;
          } = {};
          if (snapshot?.catalogueLabel) {
            before.catalogueLabel = snapshot.catalogueLabel;
          }
          if (currentEntry?.defectClassId) {
            before.defectClassId = currentEntry.defectClassId;
          }
          return updateDefect.mutateAsync({
            defectId,
            body: { defectClassId: entry.defectClassId },
            before,
            after: {
              catalogueLabel: entry.name.en,
              defectClassId: entry.defectClassId,
            },
            dataAcquisitionId: inspectionId,
            imageId: targetImageId,
          });
        }),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      outcome = { succeeded, failed, className: entry.name.en };
    }
    // For source==="aiFinding" — AI findings aren't defects yet; the
    // popover here only fires the mapper PATCH. (Promoting the finding
    // to a defect happens via Story 005's existing path.)

    // Surface outcome before closing so the snackbar parents to the
    // page, not the popover paper (which is about to unmount).
    if (source === "defect") {
      if (outcome.failed === 0 && outcome.succeeded > 0) {
        setSnack({
          kind: "success",
          copy: `${String(outcome.succeeded)} defect${outcome.succeeded === 1 ? "" : "s"} reclassified to ${outcome.className}`,
        });
      } else if (outcome.succeeded > 0 && outcome.failed > 0) {
        setSnack({
          kind: "partial",
          copy: `${String(outcome.succeeded)} succeeded, ${String(outcome.failed)} failed`,
        });
      }
    }

    if (skippedCount > 0) {
      const reason = guard.reasonForSelection(selection.ids);
      setSnack({
        kind: "skipped-cross-tenant",
        copy:
          reason ??
          `${String(skippedCount)} AI-finding tile(s) skipped — not shared with your tenant.`,
      });
    }

    onClose();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const entry = visibleEntries[focusedIndex];
      if (entry) void apply(entry);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => Math.min(i + 1, visibleEntries.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, 0));
    }
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        TransitionProps={{ onEntered: handlePopoverEntered }}
        slotProps={{
          paper: {
            // Stop the wrapping click from triggering anything on the
            // anchored row beneath.
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
            sx: { width: POPOVER_WIDTH, maxHeight: POPOVER_MAX_HEIGHT },
          },
        }}
      >
        <Stack
          sx={{ p: PADDING, gap: 1 }}
          role="dialog"
          aria-label="Reclassify defect"
          aria-modal="true"
        >
          <TextField
            inputRef={searchRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search catalogue…"
            size="small"
            type="search"
            slotProps={{
              htmlInput: {
                "aria-label": "Search catalogue entries",
                role: "searchbox",
              },
            }}
            fullWidth
          />
          <Tabs
            value={activeTab}
            onChange={(_, value: TabKey) => {
              setActiveTab(value);
            }}
            variant="fullWidth"
            aria-label="Catalogue tabs"
          >
            <Tab
              value="current"
              label={`Current${currentEntries.length > 0 ? ` (${String(currentEntries.length)})` : ""}`}
            />
            <Tab
              value="favourites"
              label={`Favourites${favouriteEntries.length > 0 ? ` (${String(favouriteEntries.length)})` : ""}`}
            />
            <Tab value="all" label="All" />
          </Tabs>
          {source === "aiFinding" ? (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={suggestMapping}
                    onChange={(e) => {
                      setSuggestMapping(e.target.checked);
                    }}
                    // MUI v6 Checkbox passes `inputProps` straight to the
                    // inner <input> — still the canonical wiring for an
                    // aria-label here. FOLLOWUP(mui-v7): switch to
                    // `slotProps.input` once that lands on Checkbox.
                    inputProps={{
                      "aria-label": `Suggest this mapping (ML class ${selection.aiClassName ?? ""}) for future findings`,
                    }}
                  />
                }
                label={
                  <Typography variant="caption">
                    Suggest this mapping
                    {selection.aiClassName
                      ? ` (${selection.aiClassName} →)`
                      : ""}{" "}
                    for future findings
                    {canApplyImmediately ? null : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{ display: "block" }}
                      >
                        Applies once the catalogue draft is published by an
                        admin.
                      </Typography>
                    )}
                  </Typography>
                }
              />
              {/* AIMAP-03 — "Apply immediately" publishes the catalogue draft
                  in the same call. Expert/admin only; hidden for pre_screener
                  (who may only suggest). Disabled until "Suggest" is ticked. */}
              {canApplyImmediately ? (
                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={applyImmediately}
                      disabled={!suggestMapping}
                      onChange={(e) => {
                        setApplyImmediately(e.target.checked);
                      }}
                      inputProps={{
                        "aria-label":
                          "Apply immediately (publish the catalogue draft now)",
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      Apply immediately
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{ display: "block" }}
                      >
                        Publishes the catalogue draft now — goes live for future
                        findings without leaving this page.
                      </Typography>
                    </Typography>
                  }
                />
              ) : null}
            </Box>
          ) : null}
          {/*
            ARIA composition (axe-clean):
              <div style="display:grid; grid-template-columns: auto 1fr">
                <div>          -- column 1: stars (one button per row)
                  <button>     -- favourite toggle (per row)
                  ...
                </div>
                <div role="listbox" aria-activedescendant=...>
                  <div role="option">name + desc</div>
                  ...
                </div>
              </div>
            Putting the favourite buttons INSIDE the listbox trips axe's
            `aria-required-children` (listbox + button). Putting them
            inside the option trips `no-focusable-content`. The grid lays
            them out as a parallel column so the visual is unchanged
            while the listbox contains only options.
          */}
          {visibleEntries.length === 0 ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No matching entries.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                alignItems: "stretch",
                overflowY: "auto",
                maxHeight: 300,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              {/* Column 1 — favourite-toggle buttons, one per row. */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {visibleEntries.map((entry, idx) => {
                  const isFav = favouriteIds.includes(entry.entryId);
                  return (
                    <Box
                      key={entry.entryId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: 44,
                        borderBottom: idx === visibleEntries.length - 1 ? 0 : 1,
                        borderColor: "divider",
                        backgroundColor:
                          idx === focusedIndex ? "action.hover" : "transparent",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          handleStarClick(e, entry);
                        }}
                        aria-label={
                          isFav
                            ? `Remove ${entry.name.en} from favourites`
                            : `Add ${entry.name.en} to favourites`
                        }
                      >
                        {/* Filled when favourited. Material carried this as
                            Star vs StarBorder; Lucide strokes one star, so
                            without the fill these two branches would render
                            identically and the state would vanish. */}
                        <StarIcon fontSize="small" filled={isFav} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
              {/* Column 2 — the listbox proper. Contains only role="option". */}
              <Box
                role="listbox"
                aria-label="Catalogue entries"
                aria-activedescendant={
                  visibleEntries[focusedIndex]
                    ? `cat-popover-row-${visibleEntries[focusedIndex].entryId}`
                    : undefined
                }
              >
                {visibleEntries.map((entry, idx) => {
                  const selectedCount =
                    source === "defect" && selection.ids.length > 1
                      ? selection.ids.length
                      : null;
                  return (
                    <Box
                      key={entry.entryId}
                      id={`cat-popover-row-${entry.entryId}`}
                      role="option"
                      aria-selected={idx === focusedIndex}
                      aria-label={entry.name.en}
                      onMouseEnter={() => {
                        setFocusedIndex(idx);
                      }}
                      onClick={() => {
                        void apply(entry);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        height: 44,
                        px: 1,
                        cursor: "pointer",
                        borderBottom: idx === visibleEntries.length - 1 ? 0 : 1,
                        borderColor: "divider",
                        backgroundColor:
                          idx === focusedIndex ? "action.hover" : "transparent",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          component="span"
                        >
                          {entry.name.en}
                        </Typography>
                        {entry.defaultDescription?.en ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {entry.defaultDescription.en}
                          </Typography>
                        ) : null}
                      </Box>
                      {selectedCount !== null ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          ×{selectedCount}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Stack>
      </Popover>
      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "success" ? 3000 : 6000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={
              snack.kind === "success"
                ? "success"
                : snack.kind === "skipped-cross-tenant"
                  ? "info"
                  : "warning"
            }
            variant="filled"
            onClose={() => {
              setSnack(null);
            }}
          >
            {snack.copy}
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
}
